import React from 'react'
import { ConfigProvider, theme } from 'antd'
import type { ThemeConfig } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { useSelector } from 'src/hooks/useRedux'
import { isCanlan } from '@sportninja/common/utils/customer-name'

const THEME = (isDarkMode: boolean): ThemeConfig => ({
  algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
  token: {
    colorPrimary: colors.DEFAULT_FLAIR,
    colorPrimaryHover: colors.PRIMARY_LIGHT,
    colorBgContainer: colors.HEADER,
    colorBorder: colors.SOFT_STEEL,
    colorLink: colors.DEFAULT_FLAIR,
    borderRadius: 4,
    borderRadiusLG: 8,
    borderRadiusSM: 4,
    borderRadiusXS: 2,
    colorError: colors.ERROR_LIGHT,
  },
  components: {
    Button: {
      primaryShadow: 'none',
      boxShadow: 'none',
      boxShadowSecondary: 'none',
      boxShadowTertiary: 'none',
      defaultShadow: 'none',
      controlHeight: 40,
      defaultBg: 'transparent',
      colorPrimaryBg: colors.PRIMARY,
      defaultBorderColor: colors.PRIMARY,
      fontFamily: !isCanlan
        ? 'BarlowCondensed, Arial, Helvetica, sans-serif !important;'
        : 'Neue Plak Condensed, Arial, Helvetica, sans-serif !important;',
    },
    Input: {
      controlHeight: 40,
      colorBgContainer: colors.SECONDARY_200,
      colorBgContainerDisabled: 'transparent',
      colorTextDisabled: colors.ATTENDANCE_GRAY,
      borderRadius: 8,
      colorBorder: colors.SECONDARY_100,
      fontFamily: !isCanlan
        ? 'Inter, Arial, Helvetica, sans-serif'
        : 'Gilroy, Arial, Helvetica, sans-serif',
      lineHeight: 1.45,
      fontSize: 16,
    },
    Select: {
      controlHeight: 40,
      colorBgContainer: colors.HEADER,
      colorBgContainerDisabled: 'transparent',
      colorTextDisabled: colors.ATTENDANCE_GRAY,
      fontFamily: !isCanlan
        ? 'Inter, Arial, Helvetica, sans-serif'
        : 'Gilroy, Arial, Helvetica, sans-serif',
      fontSize: 16,
    },
    DatePicker: {
      controlHeight: 40,
      colorBgContainer: 'transparent',
      colorBgContainerDisabled: 'transparent',
      colorTextDisabled: colors.ATTENDANCE_GRAY,
    },
    Popover: {
      colorBgElevated: colors.SOFT_STEEL,
    },
    Alert: {
      colorText: colors.WHITE,
      fontSize: 16,
      fontFamily: !isCanlan
        ? 'Inter, Arial, Helvetica, sans-serif'
        : 'Gilroy, Arial, Helvetica, sans-serif',
    },
    Message: {
      contentPadding: 16,
      colorText: colors.WHITE,
      fontSize: 16,
      fontFamily: !isCanlan
        ? 'Inter, Arial, Helvetica, sans-serif'
        : 'Gilroy, Arial, Helvetica, sans-serif',
      lineHeight: 1.2,
    },
    Layout: {
      siderBg: '#212935', // SECONDARY_BACKGROUND from Figma
      headerBg: colors.HEADER, // SECONDARY_300
      bodyBg: '#212935',
      triggerBg: colors.SECONDARY_400,
      triggerColor: colors.NEUTRAL_50,
    },
    Menu: {
      darkItemBg: 'transparent',
      darkItemSelectedBg: colors.SECONDARY_600, // #181d26
      darkItemSelectedColor: colors.PRIMARY, // Yellow
      darkItemColor: colors.NEUTRAL_0, // White
      darkItemHoverBg: colors.SECONDARY_400, // #1b222b
      darkItemHoverColor: colors.NEUTRAL_0,
      darkSubMenuItemBg: 'transparent',
      itemBorderRadius: 8,
      itemHeight: 40,
      itemMarginInline: 0,
      itemPaddingInline: 16,
      iconSize: 20,
      fontSize: 14,
      fontFamily: !isCanlan
        ? 'Inter, Arial, Helvetica, sans-serif'
        : 'Gilroy, Arial, Helvetica, sans-serif',
    },
  },
})

interface Props {
  children: React.ReactNode | JSX.Element
}

export const ThemeProvider = ({ children }: Props) => {
  const isDarkMode = useSelector((state: any) => state.app.isDarkMode)

  return <ConfigProvider theme={THEME(isDarkMode)}>{children}</ConfigProvider>
}
